import {
  BaseClickable,
  Box,
  Button,
  ChoiceCard,
  CssReset,
  Link,
  NavBar,
  SolvTheme,
  Stack,
  Text,
} from '@solvhealth/jigsaw-v1';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useMemo, useState } from 'react';
import Triage from '~/components/AccountGetsMarried/components/GetCare/components/NavTiles/components/Triage/Triage';
import {
  AlmaUrls,
  ALMA_CATEGORY,
  ALMA_DISCLAIMER,
} from '~/components/AccountGetsMarried/constants/alma';
import { consumerServiceCategories } from '~/components/AccountGetsMarried/constants/ConsumerServices/consumerServiceCategories';
import {
  ConsumerService,
  consumerServices,
} from '~/components/AccountGetsMarried/constants/ConsumerServices/consumerServices';
import { consumerServiceSubCategories } from '~/components/AccountGetsMarried/constants/ConsumerServices/consumerServiceSubCategories';
import { AlmaFaq } from '~/components/Services/AlmaFaq';
import { COVID_VACCINE_PATH } from '~/constants';
import { analyticsTrackEvent } from '~/core/analytics';
import {
  ALMA_CLICK_FIND_BUTTON_PRE_MODAL,
  ALMA_CLICK_JUMP_TO_FAQ,
  ALMA_CLICK_SEARCH,
  ALMA_CLICK_SERVICE,
  ALMA_SERVICE_PAGE_VIEWED,
  CONSUMER_SERVICE_TOUCHED,
  TRIAGE_ENTRYPOINT_VISIBLE,
  ALMA_CLICK_JUMP_TO_INSURANCE,
} from '~/core/analytics/events';
import { isUserProfileAMinor } from '~/core/util/userProfile';
import useSelectActiveUserProfile from '~/hooks/useSelectActiveUserProfile';
import useSetTitle from '~/hooks/useSetTitle';
import { useTriageEnabled } from '~/hooks/useTriageEnabled';
import { careOptionsUrl } from '~/routes/account/careTeam/CareOptionsPage';
import LinkOutModal from '~/components/LinkOutModal/LinkOutModal';
import { AlmaInsurance } from '~/components/Services/AlmaInsurance';

type Props = {
  categorySlug: string;
};

const PAGE_NAME_FOR_ANALYTICS = 'Service category detail page';
export type ServiceCategoryPageNameForAnalytics = typeof PAGE_NAME_FOR_ANALYTICS;

const serviceOnClickLink = (service: ConsumerService) => {
  if (service.href) return service.href;

  if (service.slug === 'covid-vaccine' || service.slug === 'covid-booster') {
    return COVID_VACCINE_PATH;
  }

  const serviceIdsArray = service.serviceIds?.split(',').filter((v) => !isEmpty(v));
  const shouldFilterByServiceIds = service.serviceIds && service.assumeOfferedByAllClinics;

  const getCareUrl = careOptionsUrl({
    careType: service.subCategorySlug.includes('covid-test') ? 'covid' : 'uc',
    serviceIds: shouldFilterByServiceIds ? serviceIdsArray : undefined,
    isCovidRelated: service.categorySlug === 'covid',
    isCovidTest: service.subCategorySlug.includes('covid-test'),
    isFluRelated: service.slug.includes('flu'),
    redirectToSrpIfNoOtherOptions: true,
    reasonForVisit: service.name,
  });

  return getCareUrl;
};

type ServiceChoiceCardProps = {
  service: ConsumerService;
};
const ServiceChoiceCard = ({ service }: ServiceChoiceCardProps) => {
  const href = serviceOnClickLink(service);
  const [isAlmaRedirectModalOpen, setIsAlmaRedirectModalOpen] = useState(false);
  const isAlma = service.categorySlug === ALMA_CATEGORY;
  const isCobranded = isAlma;

  const isExternal = href === COVID_VACCINE_PATH || service.externalLink;

  return (
    <>
      {isAlma && (
        <LinkOutModal
          ctaClickEvent={ALMA_CLICK_SERVICE}
          ctaClickHandler={() => {
            analyticsTrackEvent(ALMA_CLICK_SEARCH);
          }}
          isOpen={isAlmaRedirectModalOpen}
          locationName="Alma Health"
          logo={
            <img alt="Alma Health" height={40} src="/images/account/alma-logo.svg" width={116} />
          }
          onClose={() => {
            setIsAlmaRedirectModalOpen(false);
          }}
          redirectUrl={href}
        />
      )}
      <ChoiceCard
        event={CONSUMER_SERVICE_TOUCHED}
        external={href === COVID_VACCINE_PATH}
        href={isCobranded ? undefined : href}
        key={service.slug}
        meta={{
          category: service.categorySlug,
          subCategory: service.subCategorySlug,
          service: service.slug,
          highlightedServiceSection: false,
          entrypoint: 'consumer category detail page',
        }}
        onClick={() => {
          setIsAlmaRedirectModalOpen(true);
        }}
        openInNewWindow={isExternal}
        row
        style={{ paddingLeft: '0px' }}
        title={<Text variant="body">{service.name}</Text>}
      />
    </>
  );
};

export const ServiceCategoryPage = ({ categorySlug }: Props) => {
  const triageEnabled = useTriageEnabled();

  useEffect(() => {
    analyticsTrackEvent(TRIAGE_ENTRYPOINT_VISIBLE, {
      entrypoint: PAGE_NAME_FOR_ANALYTICS,
      visible: triageEnabled,
    });
  }, [triageEnabled]);

  useEffect(() => {
    if (categorySlug === ALMA_CATEGORY) {
      analyticsTrackEvent(ALMA_SERVICE_PAGE_VIEWED);
    }
  }, [categorySlug]);

  const serviceCategory = consumerServiceCategories.find(
    (category) => !category.hidden && category.slug === categorySlug
  );

  useSetTitle(serviceCategory?.name ?? 'Solv');

  const sortedServicesInCategory = consumerServices
    .filter((service) => !service.hidden && service.categorySlug === serviceCategory?.slug)
    .sort((a, b) => a.name.localeCompare(b.name));

  const subCategoriesInCategory = consumerServiceSubCategories.filter(
    (subCategory) => !subCategory.hidden && subCategory.categorySlug === serviceCategory?.slug
  );
  const popularServices = sortedServicesInCategory
    .filter((service) => service.popular)
    .sort((a, b) => {
      const aName = a.shortName ?? a.name;
      const bName = b.shortName ?? b.name;
      return aName.localeCompare(bName);
    })
    .slice(0, 5);

  const isAlma = serviceCategory?.slug === ALMA_CATEGORY;

  const isCobranded = isAlma;
  const [isAlmaRedirectModalOpen, setIsAlmaRedirectModalOpen] = useState(false);

  const isMinor = isUserProfileAMinor(useSelectActiveUserProfile());

  const description = useMemo(() => {
    if (isMinor) {
      return serviceCategory?.kids.description ?? serviceCategory?.description;
    }

    return serviceCategory?.description;
  }, [isMinor, serviceCategory?.description, serviceCategory?.kids.description]);

  return (
    <Stack pb={isCobranded ? 0 : 3} space={4} width="100%">
      <NavBar>{serviceCategory?.name ?? 'Services'}</NavBar>
      <Stack maxWidth={SolvTheme.breakpoints.sm} mx="auto" space={4} width="100%">
        {isAlma ? (
          <Stack px={3} space={1}>
            <Text variant="h3">{serviceCategory?.name}</Text>
            <Stack space={0.5}>
              <Stack alignItems="center" mt={2} row space={0.5}>
                <Text>Powered by</Text>
                <img alt="Alma logo" height={12} src="/images/account/alma-logo.png" width={44} />
              </Stack>
              <Text muted>{description}</Text>
            </Stack>
            <Stack flexWrap="wrap" row space={2}>
              <Link event={ALMA_CLICK_JUMP_TO_INSURANCE} href="#insurance">
                Accepted insurance
              </Link>
              <Link event={ALMA_CLICK_JUMP_TO_FAQ} href="#faq">
                Alma FAQs
              </Link>
            </Stack>
          </Stack>
        ) : (
          <>
            <Stack px={3} space={1}>
              <Text variant="h3">{serviceCategory?.name}</Text>
              {description && (
                <Stack space={0.5}>
                  <Text muted>{description}</Text>
                </Stack>
              )}
            </Stack>
          </>
        )}

        <Stack space={4} width="100%">
          {!isEmpty(popularServices) && (
            <Stack space={1} width="100%">
              <Text px={3} variant="subtitle">
                Popular & common services
              </Text>
              <Stack alignItems="stretch" overflow="scroll" px={3} row space={2} width="100%">
                {popularServices?.map((service) => {
                  return (
                    <BaseClickable
                      alignItems="flex-start"
                      display="flex"
                      event={CONSUMER_SERVICE_TOUCHED}
                      href={serviceOnClickLink(service)}
                      key={service.slug}
                      meta={{
                        category: service.categorySlug,
                        subCategory: service.subCategorySlug,
                        service: service.slug,
                        highlightedServiceSection: true,
                        entrypoint: 'consumer category detail page',
                      }}
                      width="72px"
                    >
                      <Stack alignItems="center" key={service.slug} space={1} textAlign="center">
                        <Box
                          borderRadius="full"
                          boxShadow="sm"
                          display="flex"
                          height="60px"
                          justifyContent="center"
                          textAlign="center"
                          width="60px"
                        >
                          <img src={`/images/consumerServices/${service.svg}`} />
                        </Box>

                        <Box
                          alignItems="center"
                          display="flex"
                          flex={1}
                          justifyContent="center"
                          textAlign="center"
                        >
                          <Text muted variant="body2">
                            {service.shortName ?? service.name}
                          </Text>
                        </Box>
                      </Stack>
                    </BaseClickable>
                  );
                })}
              </Stack>
            </Stack>
          )}
          {subCategoriesInCategory
            ?.sort((a, b) => a.sortOrder - b.sortOrder)
            .map((subCategory) => {
              const servicesInSubCategory = sortedServicesInCategory
                .filter((service) => service.subCategorySlug === subCategory.uniqueSlug)
                .sort((a, b) => {
                  if (a.sortOrder && b.sortOrder) return a.sortOrder - b.sortOrder;
                  return 0; // leave in natural order otherwise
                });
              if (isEmpty(servicesInSubCategory)) {
                return null;
              }
              return (
                <Stack key={subCategory.slug} width="100%">
                  {!isCobranded && (
                    <Text px={3} variant="subtitle">
                      {subCategory.name}
                    </Text>
                  )}
                  <Stack pl={3} width="100%">
                    {servicesInSubCategory.map((service) => (
                      <ServiceChoiceCard key={service.slug} service={service} />
                    ))}
                  </Stack>
                </Stack>
              );
            })}

          {isAlma && (
            <>
              <Stack px={3}>
                <LinkOutModal
                  ctaClickEvent={ALMA_CLICK_SEARCH}
                  isOpen={isAlmaRedirectModalOpen}
                  locationName="Alma Health"
                  logo={
                    <img
                      alt="Alma Health"
                      height={40}
                      src="/images/account/alma-logo.svg"
                      width={116}
                    />
                  }
                  onClose={() => {
                    setIsAlmaRedirectModalOpen(false);
                  }}
                  redirectUrl={AlmaUrls.search.landing}
                />
                <Button
                  event={ALMA_CLICK_FIND_BUTTON_PRE_MODAL}
                  onClick={() => setIsAlmaRedirectModalOpen(true)}
                >
                  Find care on Alma
                </Button>
              </Stack>
              <AlmaInsurance />
              <AlmaFaq />
              <Stack bg="gray-100" p={3}>
                <Text muted variant="body2">
                  {ALMA_DISCLAIMER}
                </Text>
              </Stack>
            </>
          )}

          {!isAlma && (
            <>
              {triageEnabled && (
                <Box pl={{ _: 1, sm: 3 }}>
                  <Triage entrypoint={PAGE_NAME_FOR_ANALYTICS} />
                </Box>
              )}
            </>
          )}
        </Stack>
      </Stack>
      <CssReset />
    </Stack>
  );
};
